/**
 * ConfirmDialog — themed replacement for window.confirm().
 *
 * Wraps the app's Modal so destructive actions get a consistent UI instead
 * of the browser's native blocking confirm dialog (which can't be themed
 * and looks out of place against the rest of the app).
 *
 * Usage:
 *   const [confirm, setConfirm] = useState(false);
 *
 *   <ConfirmDialog
 *     isOpen={confirm}
 *     title="Delete industry?"
 *     message={`Are you sure you want to delete "${name}"? Archived; history kept.`}
 *     confirmLabel="Delete"
 *     variant="danger"
 *     onCancel={() => setConfirm(false)}
 *     onConfirm={async () => { await mutate(); setConfirm(false); }}
 *     loading={mutation.isPending}
 *   />
 */
import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  /** Primary body text. For multi-paragraph content pass an array of strings. */
  message: string | string[];
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" tints the icon and confirm button red; "default" uses primary. */
  variant?: 'danger' | 'default';
  /** Disables the buttons and shows a spinner on confirm — pass the mutation's pending flag. */
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const isDanger = variant === 'danger';
  const Icon = isDanger ? AlertTriangle : Info;
  const iconWrap = isDanger
    ? 'bg-red-100 text-red-600'
    : 'bg-primary-100 text-primary-600';
  const paragraphs = Array.isArray(message) ? message : [message];

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => {} : onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={isDanger ? 'primary' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            className={
              isDanger
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500 border-red-600'
                : undefined
            }
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex gap-4">
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconWrap}`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 space-y-2">
          {paragraphs.map((p, idx) => (
            <p key={idx} className="text-sm text-neutral-700 leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
